import {
	renameRiskConfigAction,
	setRiskConfigIsCreating,
	setRiskConfigNameError,
} from '@Actions/Risk.actions';
import { StylovyzeDialog } from '@innovyze/stylovyze';
import { DialogProps, FormControl, Grid, TextField } from '@mui/material';
import {
	selectRiskConfig,
	selectRiskConfigNameError,
	selectRiskIsSaving,
	selectRiskSetting,
} from '@Selectors/Risk.selectors';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { RiskConfig } from '@Types';
import { useGlobalization } from '@Translations/useGlobalization';

interface Props {
	open: boolean;
	onClose: (saved: boolean) => void;
}

export const RenameRisk = ({ open, onClose }: Props) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const settings = selectRiskSetting();
	const riskConfig = selectRiskConfig();

	const [newName, setNewName] = useState('');
	const configNameError = selectRiskConfigNameError();
	const isSaving = selectRiskIsSaving();
	const wasSavingRef = useRef(false);

	const handleCancelNewModel = () => {
		onClose(false);
	};

	useEffect(() => {
		dispatch(setRiskConfigNameError(''));
		setNewName(settings.name);
	}, [settings, open]);

	useEffect(() => {
		if (
			wasSavingRef.current &&
			open &&
			!isSaving &&
			configNameError.length == 0
		) {
			onClose(true);
		}
		wasSavingRef.current = isSaving;
	}, [isSaving]);

	const handleRenameModel = () => {
		if (configNameError.length == 0) {
			dispatch(setRiskConfigIsCreating(false));
			const newSettings = { ...settings };
			newSettings.name = newName.trim();

			const newRiskConfig: RiskConfig = {
				...riskConfig,
				riskSetting: newSettings,
			};
			dispatch(renameRiskConfigAction(newRiskConfig));
		}
	};

	const handlelNameChangeValue = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		let error = '';
		if (event.target && event.target.value !== '') {
			setNewName(event.target.value);
			const rexp = new RegExp('^[a-zA-Z0-9 ]+$');
			const test = rexp.test(event.target.value);
			if (!test) {
				error = t('Only alphanumeric characters are allowed');
			}

			const rexp2 = new RegExp('^.{1,50}$');
			const test2 = rexp2.test(event.target.value);
			if (!test2) {
				error = t('Name cannot contain more than 50 characters');
			}
		} else {
			setNewName('');
			error = t('Name is required');
		}
		dispatch(setRiskConfigNameError(error));
	};

	// This stops the dialog closing when clicked on the background
	const onCloseDialog = () => void {};

	const dialogProps: DialogProps = {
		open: open,
		fullWidth: true,
		maxWidth: 'sm',
		onClose: onCloseDialog,
	};

	return (
		<StylovyzeDialog
			dialogProps={dialogProps}
			data-cy="new-dialog"
			title={t('Rename Risk Model')}
			open={open}
			cancelText={t('Cancel')}
			cancelButtonProps={{
				disabled: isSaving,
				dataCy: 'rename-cancel',
			}}
			onCancel={handleCancelNewModel}
			confirmText={t('OK')}
			confirmButtonProps={{
				disabled:
					isSaving ||
					configNameError.length !== 0 ||
					settings.name.trim().toLowerCase() ===
						newName.trim().toLowerCase(),
				dataCy: 'rename-confirm',
			}}
			onConfirm={handleRenameModel}>
			<Grid container>
				<Grid item xs={12}>
					<FormControl variant="outlined" margin="normal" fullWidth>
						<TextField
							fullWidth
							defaultValue={settings.name}
							error={configNameError.length !== 0}
							required
							name="name"
							label={t('Edit Name')}
							variant="outlined"
							onChange={handlelNameChangeValue}
							helperText={configNameError}
						/>
					</FormControl>
				</Grid>
			</Grid>
		</StylovyzeDialog>
	);
};
